import { FormattedHTMLMessage } from '@99designs/common/i18n';
import { SquarespaceApiBrief as SquarespaceApiBriefFragment } from '__generated__/SquarespaceApiBrief';
import { BriefState } from '__generated__/types';
import { RecommendationLoginModal } from 'common/components/SquarespaceModal';
import { handlePromise } from 'common/utils/Bugsnag';
import { Source } from 'launch/consts/signupSource';
import { AuthContext } from 'projects/components/AuthContext';
import BriefContext from 'projects/components/BriefContext';
import ColorComponent from 'projects/components/Color';
import Header from 'projects/components/Header';
import Layout from 'projects/components/Layout';
import SquarespaceLaunchModal, { LaunchState } from 'projects/components/SquarespaceLaunchModal';
import { Color } from 'projects/consts';
import { trackViewedBriefEvent } from 'projects/events';
import { redirectLaunchedStructuredBrief } from 'projects/routes';
import qs from 'qs';
import React, { useContext, useEffect } from 'react';
import { useApolloClient } from 'react-apollo';
import { useLocation } from 'react-router';
import SquarespaceDesignersPage from './SquarespaceDesignersPage';
import useWorkflowMutations from './useWorkflowMutations';

export interface Props {
    brief: SquarespaceApiBriefFragment;
}

function useSquarespaceLogin() {
    const client = useApolloClient();
    const { login } = useContext(AuthContext);
    const location = useLocation();

    return async function squarespaceLogin() {
        const props = {
            source: Source.SQUARESPACE_LAUNCH,
            signupHeading: (
                <div
                    dangerouslySetInnerHTML={{
                        __html: require('@99designs/common/core/images/99d-x-sqsp-wordmarks-dark.svg'),
                    }}
                />
            ),
            signupMessage: (
                <div className="auth-description">
                    <ColorComponent color={Color.squarespaceBlack}>
                        <div className="heading heading--h3">
                            {__('Create a 99designs account to see your matches!')}
                        </div>
                    </ColorComponent>
                    <ColorComponent color={Color.puntRoad}>
                        <div className="paragraph paragraph--small">
                            <FormattedHTMLMessage
                                message={__(
                                    "Now that you've completed your <strong>Squarespace</strong> website project brief you're just one click away from connecting with qualified <strong>Squarespace</strong> designers."
                                )}
                            />
                        </div>
                        <div className="paragraph paragraph--small">
                            <FormattedHTMLMessage
                                message={__(
                                    "Once you've created your 99designs account, you'll be able to review <strong>Squarespace</strong> designers and start receiving quotes in just <strong>24 hours</strong>."
                                )}
                            />
                        </div>
                    </ColorComponent>
                </div>
            ),
            ctaColor: Color.squarespaceBlack,
            ctaMessage: __('Create account'),
            loginLinkMessage: __('Already have a 99designs account?'),
            showHeaderDivider: false,
            modalComponent: RecommendationLoginModal,
            postLogin: async () => {
                await client.resetStore();
            },
        };

        // This is a "temporary" method for prefilling a user's email from
        // squarespace via query parameter.
        const query = location.search.slice(1);
        const { email } = qs.parse(query);

        return login({ email, ...props });
    };
}

/**
 * Squarespace project creation flow for *recommending designers* for an existing brief.
 */
function RecommendationWorkflow({ brief }: Props) {
    const location = useLocation();
    const [launchState, launch, claim] = useWorkflowMutations(brief);
    const login = useSquarespaceLogin();

    function trackEvent() {
        const query = location.search.slice(1);
        const { email } = qs.parse(query);

        trackViewedBriefEvent({
            id: brief.id,
            page: 'SquarespaceDesigners',
            initial_email_address: email,
        });
    }

    async function launchWithDelay() {
        // Due to the way Your Work is wired up it can take a while for new
        // project work items to replicate into workbench.
        // As an interim solution this adds a short delay to help mitigate the issue
        // of seeing zero invites on Your Work after launching your brief.
        await launch({ delay: 5000 });
    }

    useEffect(() => {
        trackEvent();

        handlePromise(
            (async function() {
                await login();
                await claim();
            })(),
            RecommendationWorkflow.name
        );
    }, []);

    if (brief.state == BriefState.LAUNCHED && !launchState) {
        redirectLaunchedStructuredBrief(brief);
        return null;
    }

    const showModal = launchState && [LaunchState.Error, LaunchState.Success].includes(launchState);

    return (
        <Layout>
            <BriefContext.Provider
                value={{ brief, themeColor: Color.squarespaceBlack, themeName: 'squarespace' }}
            >
                <Header title={'Invite designers to quote | 99designs'}>
                    <ColorComponent color={Color.squarespaceBlack}>
                        <div className="heading heading--h4 heading--no-margin">
                            <FormattedHTMLMessage
                                message={__(
                                    'Welcome to 99designs!<br />We’ve got awesome Squarespace designers that match your brief'
                                )}
                            />
                        </div>
                    </ColorComponent>
                </Header>

                <SquarespaceDesignersPage brief={brief} launch={launchWithDelay} />

                <SquarespaceLaunchModal state={launchState!} brief={brief} showing={!!showModal} />
            </BriefContext.Provider>
        </Layout>
    );
}

export default RecommendationWorkflow;
